import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Grid3X3, Trophy, AlertTriangle, Scale, Info } from "lucide-react";
import { cn } from "@/lib/utils";

interface MatrixEntry {
  criterion: string;
  weight?: number;
  scores: Array<{
    option_id: string;
    score: string;
    points: number;
    weighted_points?: number;
  }>;
}

interface OptionTotal {
  option_id: string;
  label: string;
  total_points: number;
  weighted_total?: number;
  max_points: number;
}

interface TieBreaker {
  has_tie?: boolean;
  tied_options?: string[];
  resolution_method?: "highest_weighted_criterion" | "hypothesis_fit" | "strategic_override" | "risk_preference" | "reversibility";
  resolution_explanation?: string;
  winning_criterion?: string;
}

interface TradeOff {
  criterion: string;
  leader_option: string;
  loser_option: string;
  description: string;
}

interface EvaluationMatrixProps {
  data: {
    matrix_view?: MatrixEntry[];
    criteria_used?: string[];
    criteria_label_map?: Record<string, string>;
    option_totals?: OptionTotal[];
    weighted?: boolean;
    tie_breaker?: TieBreaker;
    trade_offs?: TradeOff[];
    source?: "phase3" | "ai_generated";
    scale?: number;
  };
  options?: Array<{
    option_id: string;
    label: string;
  }>;
  recommendedOptionId?: string;
}

const CRITERIA_CONFIG: Record<string, { label: string; weight: number; description: string }> = {
  value_potential: { label: "Wertpotenzial", weight: 40, description: "Erwarteter Geschäftswert bei erfolgreicher Umsetzung" },
  risk_profile: { label: "Risikoprofil", weight: 30, description: "Bewertung der Risiken (5 = niedriges Risiko)" },
  execution_fit: { label: "Umsetzungs-Fit", weight: 20, description: "Passung zu vorhandenen Ressourcen und Fähigkeiten" },
  strategic_alignment: { label: "Strategische Passung", weight: 10, description: "Alignment mit langfristigen Unternehmenszielen" },
  market_timing: { label: "Markt-Timing", weight: 25, description: "Zeitliche Passung zum Marktfenster" },
  resource_efficiency: { label: "Ressourceneffizienz", weight: 20, description: "Verhältnis von Aufwand zu erwartetem Nutzen" },
  scalability: { label: "Skalierbarkeit", weight: 15, description: "Potenzial für zukünftiges Wachstum" },
  reversibility: { label: "Reversibilität", weight: 15, description: "Möglichkeit zur Kurskorrektur" },
  validation_speed: { label: "Validierungsgeschwindigkeit", weight: 25, description: "Wie schnell kann die Hypothese getestet werden" },
  customer_value: { label: "Kundenwert", weight: 30, description: "Direkter Nutzen für Zielkunden" },
  learning_potential: { label: "Lernpotenzial", weight: 20, description: "Erkenntnisgewinn für zukünftige Entscheidungen" },
  cost_efficiency: { label: "Kosteneffizienz", weight: 25, description: "Verhältnis von Investition zu erwartetem Return" },
  market_fit: { label: "Markt-Fit", weight: 30, description: "Passung zum aktuellen Marktbedarf" },
  competitive_edge: { label: "Wettbewerbsvorteil", weight: 20, description: "Differenzierung vom Wettbewerb" },
  team_readiness: { label: "Team-Bereitschaft", weight: 15, description: "Verfügbarkeit und Kompetenz des Teams" },
  hypothesis_fit: { label: "Hypothesen-Fit", weight: 35, description: "Passung zur Kernhypothese" },
};

const getCriterionConfig = (criterion: string, weight?: number) => {
  if (CRITERIA_CONFIG[criterion]) {
    return CRITERIA_CONFIG[criterion];
  }
  const label = sanitizeSnakeCase(criterion);
  return {
    label,
    weight: weight || 25,
    description: `Bewertungskriterium: ${label}`
  };
};

// Fix 6: Sanitize snake_case to readable labels
const sanitizeSnakeCase = (text: string): string => {
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Sanitize a full description string that may contain snake_case tokens
const sanitizeDescription = (desc: string, criteriaLabelMap: Record<string, string>): string => {
  // Replace known criterion keys with their labels
  let result = desc;
  for (const [key, label] of Object.entries(criteriaLabelMap)) {
    result = result.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), label);
  }
  for (const [key, config] of Object.entries(CRITERIA_CONFIG)) {
    result = result.replace(new RegExp(key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), config.label);
  }
  // Catch remaining snake_case tokens
  result = result.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, match => sanitizeSnakeCase(match));
  return result;
};

const RESOLUTION_METHOD_LABELS: Record<string, string> = {
  highest_weighted_criterion: "Höchstes Gewicht entscheidet",
  hypothesis_fit: "Passung zur Kernhypothese",
  strategic_override: "Strategische Übersteuerung",
  risk_preference: "Risikoaversion als Meta-Kriterium",
  reversibility: "Reversibilität als Entscheidungshilfe",
};

// Fix 1: Weighted score moved to tooltip only
const PointDisplay = ({ points, weighted, maxScale = 5 }: { points: number; weighted?: number; maxScale?: number }) => {
  const highThreshold = maxScale === 3 ? 3 : 4;
  const midThreshold = maxScale === 3 ? 2 : 3;
  
  const color = points >= highThreshold ? "text-green-600" : points >= midThreshold ? "text-amber-600" : "text-red-600";
  const bgColor = points >= highThreshold ? "bg-green-500" : points >= midThreshold ? "bg-amber-500" : "bg-red-500";
  
  const dots = Array.from({ length: maxScale }, (_, i) => i + 1);

  const content = (
    <div className="flex items-center justify-center gap-1.5">
      <div className="flex gap-0.5">
        {dots.map(i => (
          <div
            key={i}
            className={cn(
              "w-1.5 h-1.5 rounded-full",
              i <= points ? bgColor : "bg-muted/30"
            )}
          />
        ))}
      </div>
      <span className={cn("text-xs font-bold", color)}>
        {points}
      </span>
    </div>
  );

  // If weighted value exists, show in tooltip only
  if (weighted !== undefined) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="cursor-help">{content}</div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          Gewichtet: {weighted.toFixed(1)} Punkte
        </TooltipContent>
      </Tooltip>
    );
  }

  return content;
};

const checkAllScoresEqual = (optionTotals: OptionTotal[]) => {
  if (optionTotals.length < 2) return false;
  const firstScore = optionTotals[0].weighted_total ?? optionTotals[0].total_points;
  return optionTotals.every(t => {
    const score = t.weighted_total ?? t.total_points;
    return Math.abs(score - firstScore) < 0.1;
  });
};

export function EvaluationMatrix({ data, options, recommendedOptionId }: EvaluationMatrixProps) {
  if (!data?.matrix_view || data.matrix_view.length === 0) return null;

  const optionsList = options || data.option_totals?.map((t: OptionTotal) => ({
    option_id: t.option_id,
    label: t.label
  })) || data.matrix_view[0]?.scores?.map((s, i) => ({
    option_id: s.option_id,
    label: `Option ${String.fromCharCode(65 + i)}`
  })) || [];

  const optionTotals = data.option_totals || [];
  const winnerOption = optionTotals.length > 0
    ? optionTotals.reduce((best, curr) => {
        const currScore = curr.weighted_total ?? curr.total_points;
        const bestScore = best.weighted_total ?? best.total_points;
        return currScore > bestScore ? curr : best;
      }, optionTotals[0])
    : null;

  const showWeights = data.weighted !== false;
  const tieBreaker = data.tie_breaker;
  const allScoresEqual = checkAllScoresEqual(optionTotals);
  const isFromPhase3 = data.source === "phase3" || !data.source;
  // Fix 2: Default scale 5
  const maxScale = data.scale || 5;
  const criteriaLabelMap = data.criteria_label_map || {};

  // Fix 5: Calculate margin to second place
  const getMarginToSecond = (): { margin: number; isClose: boolean } | null => {
    if (optionTotals.length < 2 || !winnerOption) return null;
    const winnerScore = winnerOption.weighted_total ?? winnerOption.total_points;
    const others = optionTotals
      .filter(t => t.option_id !== winnerOption.option_id)
      .map(t => t.weighted_total ?? t.total_points);
    const secondBest = Math.max(...others);
    const margin = winnerScore - secondBest;
    return { margin, isClose: margin < 0.5 };
  };

  const marginInfo = getMarginToSecond();

  const detectTradeOffs = (): TradeOff[] => {
    const tradeOffs: TradeOff[] = [];
    if (!data.matrix_view || optionsList.length < 2) return tradeOffs;

    data.matrix_view.forEach(row => {
      const scores = row.scores;
      if (scores.length < 2) return;

      const maxPoints = Math.max(...scores.map(s => s.points));
      const leaders = scores.filter(s => s.points === maxPoints);
      const losers = scores.filter(s => s.points < maxPoints);

      if (leaders.length === 1 && losers.length > 0) {
        const leaderLabel = optionsList.find(o => o.option_id === leaders[0].option_id)?.label || "Option";
        const loserWithMinPoints = losers.reduce((min, curr) => curr.points < min.points ? curr : min, losers[0]);
        const loserLabel = optionsList.find(o => o.option_id === loserWithMinPoints.option_id)?.label || "Option";
        
        if (maxPoints - loserWithMinPoints.points >= 2) {
          // Fix 6: Use readable labels
          const displayLabel = criteriaLabelMap[row.criterion] || getCriterionConfig(row.criterion, row.weight).label;
          tradeOffs.push({
            criterion: row.criterion,
            leader_option: leaderLabel,
            loser_option: loserLabel,
            description: `${leaderLabel} führt bei ${displayLabel}, ${loserLabel} schwächer`
          });
        }
      }
    });

    return tradeOffs;
  };

  // Fix 6: Sanitize trade-off descriptions from data
  const rawTradeOffs = data.trade_offs || detectTradeOffs();
  const tradeOffs = rawTradeOffs.map(t => ({
    ...t,
    description: sanitizeDescription(t.description, criteriaLabelMap)
  }));

  return (
    <TooltipProvider>
      <Card id="matrix" className="scroll-mt-24">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Grid3X3 className="h-5 w-5 text-primary" />
                Bewertungsmatrix
                {showWeights && (
                  <span className="text-xs font-normal text-muted-foreground ml-2">
                    (gewichtet)
                  </span>
                )}
              </CardTitle>
              {isFromPhase3 && (
                <Badge variant="secondary" className="text-[10px]">
                  Exakt aus Phase 3
                </Badge>
              )}
            </div>
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5 user-input-compact">
              <Info className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600" />
              <span>
                <strong>Aus Nutzereingabe (Phase 3):</strong> Diese Matrix zeigt die Team-Einschätzung. 
                Höhere Werte = bessere Erfüllung (1–{maxScale} Skala). {showWeights ? "Gewichtung spiegelt strategische Priorität." : ""}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
        {allScoresEqual && !tieBreaker?.has_tie && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-sm text-red-700 dark:text-red-400 mb-1">
                  Matrix liefert keine Differenzierung
                </h4>
                <p className="text-sm text-foreground">
                  Alle Optionen haben identische Bewertungen. Die Empfehlung basiert auf Meta-Kriterien außerhalb der Matrix.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[200px]">Kriterium</TableHead>
                {showWeights && <TableHead className="w-[60px] text-center text-xs">Gewicht</TableHead>}
                {optionsList.map((option, index) => {
                  const isWinner = winnerOption?.option_id === option.option_id || 
                    recommendedOptionId === option.option_id;
                  return (
                    <TableHead 
                      key={option.option_id || index} 
                      className={cn(
                        "text-center min-w-[100px]",
                        isWinner && "bg-primary/5"
                      )}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {isWinner && <Trophy className="h-3 w-3 text-primary" />}
                        <span className={cn("text-xs", isWinner && "font-semibold text-primary")}>
                          {option.label}
                        </span>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
            {data.matrix_view.map((row, rowIndex) => {
                const labelFromData = criteriaLabelMap[row.criterion];
                const config = getCriterionConfig(row.criterion, row.weight);
                const displayLabel = labelFromData || (row as any).label || config.label;
                const weight = row.weight ?? config.weight;
                
                const hasTradeOff = tradeOffs.some(t => t.criterion === row.criterion);
                
                return (
                  <TableRow key={row.criterion || rowIndex} className={cn(hasTradeOff && "bg-amber-500/5")}>
                    <TableCell className="font-medium text-sm">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex items-center gap-1.5 cursor-help">
                            {displayLabel}
                            {hasTradeOff && <Scale className="h-3 w-3 text-amber-500" />}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p className="text-xs">{config.description}</p>
                          {hasTradeOff && (
                            <p className="text-xs text-amber-600 mt-1 font-medium flex items-center gap-1">
                              <Scale className="h-3 w-3" /> Zielkonflikt erkannt
                            </p>
                          )}
                        </TooltipContent>
                      </Tooltip>
                    </TableCell>
                    {/* Fix 3: Weight cell with tooltip explanation */}
                    {showWeights && (
                      <TableCell className="text-center text-xs text-muted-foreground font-medium">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help border-b border-dotted border-muted-foreground/40">
                              {weight}%
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs font-medium">{displayLabel}: {weight}%</p>
                            <p className="text-xs text-muted-foreground mt-0.5">{config.description}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                    )}
                    {row.scores.map((scoreData, colIndex) => {
                      const isWinner = winnerOption?.option_id === scoreData.option_id ||
                        recommendedOptionId === scoreData.option_id;
                      const weightedPoints = showWeights 
                        ? (scoreData.points * weight) / 100 
                        : undefined;
                      
                      return (
                        <TableCell 
                          key={`${row.criterion}-${colIndex}`} 
                          className={cn(
                            "text-center py-2",
                            isWinner && "bg-primary/5"
                          )}
                        >
                          <PointDisplay 
                            points={scoreData.points || 2} 
                            weighted={weightedPoints}
                            maxScale={maxScale}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
              
              {/* Total Row */}
              {optionTotals.length > 0 && (
                <TableRow className="border-t-2 border-primary/20 bg-muted/30">
                  <TableCell className="font-bold text-sm">
                    Gesamt
                  </TableCell>
                  {showWeights && <TableCell className="text-center text-xs font-bold">100%</TableCell>}
                  {optionTotals.map((total, index) => {
                    const isWinner = winnerOption?.option_id === total.option_id ||
                      recommendedOptionId === total.option_id;
                    const displayScore = total.weighted_total ?? total.total_points;
                    const maxScore = total.weighted_total !== undefined ? maxScale : total.max_points;
                    
                    return (
                      <TableCell 
                        key={total.option_id || index}
                        className={cn(
                          "text-center",
                          isWinner && "bg-primary/10"
                        )}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <div className="flex items-center justify-center gap-1">
                            {isWinner && <Trophy className="h-4 w-4 text-primary" />}
                            <span className={cn(
                              "text-lg font-bold",
                              isWinner ? "text-primary" : ""
                            )}>
                              {displayScore.toFixed(total.weighted_total !== undefined ? 1 : 0)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              /{maxScore}
                            </span>
                          </div>
                          {/* Fix 5: Margin to second place */}
                          {isWinner && marginInfo && !allScoresEqual && (
                            <span className={cn(
                              "text-[10px]",
                              marginInfo.isClose ? "text-amber-600" : "text-muted-foreground"
                            )}>
                              {marginInfo.isClose
                                ? `Knapper Vorsprung (+${marginInfo.margin.toFixed(1)})`
                                : `Vorsprung: +${marginInfo.margin.toFixed(1)}`
                              }
                            </span>
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Score Bars - Visual comparison */}
        {optionTotals.length > 0 && (
          <div className="space-y-2 pt-2">
            <p className="text-xs font-medium text-muted-foreground">Score-Vergleich</p>
            {optionTotals
              .slice()
              .sort((a, b) => (b.weighted_total ?? b.total_points) - (a.weighted_total ?? a.total_points))
              .map((total) => {
                const score = total.weighted_total ?? total.total_points;
                const maxPossible = total.weighted_total !== undefined ? maxScale : total.max_points;
                const pct = maxPossible > 0 ? Math.round((score / maxPossible) * 100) : 0;
                const isWinner = winnerOption?.option_id === total.option_id || recommendedOptionId === total.option_id;

                return (
                  <div key={total.option_id} className="flex items-center gap-3">
                    <span className={cn(
                      "text-xs w-[140px] truncate shrink-0",
                      isWinner ? "font-semibold text-primary" : "text-muted-foreground"
                    )}>
                      {total.label}
                    </span>
                    <div className="flex-1 h-5 bg-muted/40 rounded-full overflow-hidden">
                      <div
                        className={cn(
                          "h-full rounded-full transition-all duration-700",
                          isWinner ? "bg-primary" : "bg-muted-foreground/30"
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={cn(
                      "text-xs font-bold w-[48px] text-right shrink-0",
                      isWinner ? "text-primary" : "text-muted-foreground"
                    )}>
                      {pct}%
                    </span>
                  </div>
                );
              })}
          </div>
        )}

        {/* Fix 4: Scale legend */}
        <p className="text-[11px] text-muted-foreground text-center">
          Skala: 1 = schwach / {maxScale} = stark (bei Risiko-Kriterien: {maxScale} = niedriges Risiko)
        </p>

        {/* Trade-off Summary */}
        {tradeOffs.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Scale className="h-4 w-4 text-amber-600" />
              <h4 className="font-semibold text-sm text-amber-700 dark:text-amber-400">
                Erkannte Zielkonflikte
              </h4>
            </div>
            <ul className="space-y-1 text-xs">
              {tradeOffs.slice(0, 3).map((tradeOff, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Scale className="h-3 w-3 text-amber-500 shrink-0" />
                  <span>{tradeOff.description}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tie-Breaker Explanation */}
        {tieBreaker?.has_tie && tieBreaker.resolution_explanation && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Scale className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-sm text-amber-700 dark:text-amber-400">
                    Gleichstand aufgelöst
                  </h4>
                  {tieBreaker.resolution_method && (
                    <span className="text-xs text-muted-foreground">
                      ({RESOLUTION_METHOD_LABELS[tieBreaker.resolution_method] || tieBreaker.resolution_method})
                    </span>
                  )}
                </div>
                <p className="text-sm text-foreground">
                  {tieBreaker.resolution_explanation}
                </p>
                {tieBreaker.winning_criterion && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <strong>Entscheidendes Kriterium:</strong> {criteriaLabelMap[tieBreaker.winning_criterion] || CRITERIA_CONFIG[tieBreaker.winning_criterion]?.label || sanitizeSnakeCase(tieBreaker.winning_criterion)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
